import { Button } from '@droplet-tech-code/core-elements/module/ui/Button';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import {
  FormBuilder,
  FormHandler,
} from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import {
  Modal,
  useModal,
  useModalVerify,
} from '@droplet-tech-code/core-elements/module/ui/Modal';
import { VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { showToast } from '@droplet-tech-code/core-elements/module/ui/Toast';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import { UpdateCustomerRequest } from '@naus-code/naus-admin-types';
import { useRef } from 'react';

import { FormContent } from '~/components/Form/Form.Container';
import { ModalScrollView } from '~/components/Modals/Modal.utils';
import { useGetAuthUserRole, useGetAuthUserRules } from '~/screens/Auth/Auth.Context';
import { translate } from '~/utils/translation.utils';

import { customerApi } from '../utils/Customer.api';
import { useIsSystemUser } from '~/hooks/auth.hook';

const updateCustomerModalKey = 'UpdateCustomer-ModalKey';

export const useUpdateCustomerModal = () => {
  const { openModal, closeModal } = useModal();

  return {
    open: ({ customerId, email }: { customerId: string; email?: string }) => {
      openModal(<UpdateCustomerComponent customerId={customerId} email={email} />, {
        type: 'action-sheet-full',
        key: updateCustomerModalKey,
      });
    },
    close: () => {
      closeModal(updateCustomerModalKey);
    },
  };
};

const UpdateCustomerComponent = ({
  customerId,
  email,
}: {
  customerId: string;
  email?: string;
}) => {
  const formRef = useRef(new FormHandler());
  const { closeModal } = useModal();
  const { data: customerFormConfig, isLoading: isLoadingForm } =
    customerApi.useUpdateCustomerFormConfigQuery(customerId);
  const [updateCustomer, { isLoading: isUpdatingCustomer }] =
    customerApi.useUpdateCustomerMutation();

  if (isLoadingForm) {
    return <LoadingIndicator />;
  }

  if (!customerFormConfig) {
    return null;
  }

  return (
    <Modal.Container>
      <Modal.Header
        title={translate('customer.updateCustomer')}
        onPress={() => {
          closeModal(updateCustomerModalKey);
        }}
      />
      <ModalScrollView>
        <FormContent isLoading={isLoadingForm} form={customerFormConfig} flex={1}>
          <FormBuilder data={customerFormConfig} form={formRef.current} />
        </FormContent>
        <DeleteCustomerAccount customerId={customerId} email={email} />
      </ModalScrollView>
      <Modal.Footer
        divider
        isLoading={isUpdatingCustomer}
        onConfirm={async () => {
          const { valid, value } =
            formRef.current.getValue<UpdateCustomerRequest['customer']>();

          if (valid) {
            const response = await updateCustomer({
              customer: { ...value, _id: customerId },
            });
            handleResponse({
              response,
              onSuccess: () => {
                closeModal(updateCustomerModalKey);
              },
              onError: () => {
                closeModal(updateCustomerModalKey);
              },
            });
          }
        }}
        onCancel={() => closeModal(updateCustomerModalKey)}
      />
    </Modal.Container>
  );
};

const DeleteCustomerAccount = ({
  customerId,
  email,
}: {
  customerId: string;
  email?: string;
}) => {
  const canDeleteCustomer = useGetAuthUserRules('canDeleteCustomer');
  const isAdminOrSys = useIsSystemUser() || useGetAuthUserRole() === 'ADMIN';
  const { closeModal } = useModal();
  const { verifyAction } = useModalVerify();
  const [deleteCustomerAccount, { isLoading }] =
    customerApi.useDeleteCustomerAccountMutation();

  if (!canDeleteCustomer && !isAdminOrSys) {
    return null;
  }

  return (
    <VStack space="2.5" p="2.5">
      <Divider />
      <Button
        color="error"
        isLoading={isLoading}
        onPress={async () => {
          const verify = await verifyAction({
            title: email || customerId,
            description: translate('customer.deleteAccountConfirm'),
          });

          if (!verify) {
            return;
          }

          const response = await deleteCustomerAccount({ customerId });

          handleResponse({
            response,
            disableSuccessToast: true,
            onSuccess: ({ data }) => {
              showToast(
                [
                  translate('customer.deleteAccountSummary'),
                  `${translate(
                    'customer.deleteAccountPaymentMethodsRemoved',
                  )}: ${data.paymentMethodsRemoved}`,
                  `${translate('customer.deleteAccountPaymentMethodsFailed')}: ${
                    data.paymentMethodsFailed
                  }`,
                  `${translate('customer.deleteAccountBookingsUpdated')}: ${
                    data.bookingsUpdated
                  }`,
                ].join(' | '),
              );
              closeModal(updateCustomerModalKey);
            },
          });
        }}
      >
        {translate('customer.deleteAccount')}
      </Button>
    </VStack>
  );
};
